import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ShieldCheck, Sparkles, Rocket, Crown, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { digitalOcean } from "@/integrations/digitalocean/client";
// Detect if running as a native mobile app (iOS/Android via Capacitor)
const getIsNativeApp = () => {
  try {
    if (typeof window !== 'undefined' && window.Capacitor) {
      return window.Capacitor.isNativePlatform();
    }
    return false;
  } catch {
    return false;
  }
};
const isNativeApp = getIsNativeApp();

const PRODUCTS = [
  {
    id: "points_pack_5",
    name: "$5 Points Pack",
    price: 5,
    description: "Get Feedback, Submit to Playlists, and more.",
    badge: "Starter",
    perks: ["Instant delivery to your balance", "Submit tracks to real curators", "Get honest feedback on your music"],
    icon: <Sparkles className="w-5 h-5 text-[#d0d0d0]" />,
  },
  {
    id: "points_pack_15",
    name: "$15 Points Pack",
    price: 15,
    description: "Pay $15, get $20 in credits. Best value for serious artists.",
    badge: "Best Value",
    highlight: true,
    perks: ["$20 worth of credits (33% bonus!)", "Instant delivery to your balance", "Submit more tracks, get more feedback"],
    icon: <Crown className="w-5 h-5 text-[#7f5af0]" />,
  },
  {
    id: "growth_package_199",
    name: "Custom Growth Package",
    price: 199,
    description: "Guaranteed growth if you follow our plan exactly.",
    badge: "Annual Package",
    perks: ["Access to our crossposting tools", "Free credits to our lyric video generator", "Free access to our mastering tool", "50 free credits included"],
    icon: <Rocket className="w-5 h-5 text-[#d0d0d0]" />,
    oneTime: true,
  },
];

// Website URL for purchases (used on native mobile apps)
const PURCHASE_WEBSITE_URL = "https://soundope.com/shop";

export default function Shop() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [processingProduct, setProcessingProduct] = useState(null);
  const [buttonsRendered, setButtonsRendered] = useState({});
  const renderAttemptedRef = useRef({});

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await digitalOcean.auth.getSession();
      if (!session) {
        navigate("/Auth");
        return;
      }

      const { data: profile } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      setCurrentUser(profile);
    };

    loadUser();
  }, [navigate]);

  // Check for PayPal SDK (already loaded in index.html)
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    // Don't check PayPal SDK on native apps - we'll redirect to website instead
    if (isNativeApp) {
      console.log('[PayPal] Skipping SDK check - native app detected');
      return;
    }
    
    // Check if already loaded (from index.html)
    if (window.paypal?.Buttons) {
      console.log('[PayPal] SDK already loaded');
      setPaypalReady(true);
      return;
    }

    // Wait for PayPal to initialize (it's loaded async in index.html)
    console.log('[PayPal] Waiting for SDK to initialize...');
    const checkReady = setInterval(() => {
      if (window.paypal?.Buttons) {
        console.log('[PayPal] Buttons API available');
        clearInterval(checkReady);
        setPaypalReady(true);
      }
    }, 100);
    
    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkReady);
      if (!window.paypal?.Buttons) {
        console.error('[PayPal] Failed to load Buttons API');
        toast.error("Failed to load PayPal. Please refresh the page.");
      }
    }, 10000);

    return () => clearInterval(checkReady);
  }, []);

  // Render PayPal buttons
  const renderPayPalButton = useCallback((product) => {
    const containerId = `paypal-button-${product.id}`;
    const container = document.getElementById(containerId);
    
    if (!container || !window.paypal?.Buttons || !currentUser) {
      return;
    }

    // Prevent duplicate renders
    if (renderAttemptedRef.current[product.id]) {
      return;
    }
    renderAttemptedRef.current[product.id] = true;

    console.log(`[PayPal] Rendering button for ${product.id}`);

    try {
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'black',
          shape: 'rect',
          label: 'paypal',
          height: 45,
        },
        createOrder: async () => {
          try {
            setProcessingProduct(product.id);
            
            const { data: { session } } = await digitalOcean.auth.getSession();
            if (!session?.access_token) {
              throw new Error('Please log in to make a purchase');
            }

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-create-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ product_id: product.id }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to create order');
            }

            const data = await response.json();
            console.log(`[PayPal] Order created: ${data.orderID}`);
            return data.orderID;
          } catch (error) {
            console.error('Create order error:', error);
            toast.error(error.message || 'Failed to start checkout');
            setProcessingProduct(null);
            throw error;
          }
        },
        onApprove: async (data) => {
          try {
            console.log(`[PayPal] Payment approved, capturing order ${data.orderID}`);
            
            const { data: { session } } = await digitalOcean.auth.getSession();
            if (!session?.access_token) {
              throw new Error('Session expired. Please log in again.');
            }

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-capture-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ orderID: data.orderID, product_id: product.id }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to capture payment');
            }

            const result = await response.json();
            console.log('[PayPal] Capture result:', result);
            
            if (result.success) {
              toast.success(`Payment successful! $${(result.credits_awarded / 100).toFixed(2)} added to your review credits balance.`);
              
              // Refresh user data
              const { data: profile } = await digitalOcean
                .from("profiles")
                .select("*")
                .eq("id", currentUser.id)
                .single();
              if (profile) setCurrentUser(profile);
              
              // Dispatch event to refresh header balance
              window.dispatchEvent(new CustomEvent('credits:updated', { detail: { newBalance: result.new_balance } }));
              
              // Navigate to purchase history
              navigate('/PurchaseHistory?success=true');
            } else {
              throw new Error('Payment processing failed');
            }
          } catch (error) {
            console.error('Capture error:', error);
            toast.error(error.message || 'Payment failed. Please contact support.');
          } finally {
            setProcessingProduct(null);
          }
        },
        onCancel: () => {
          console.log('[PayPal] Payment cancelled');
          setProcessingProduct(null);
          toast.info('Payment cancelled');
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          setProcessingProduct(null);
          toast.error('PayPal error. Please try again.');
        },
      }).render(`#${containerId}`).then(() => {
        console.log(`[PayPal] Button rendered for ${product.id}`);
        setButtonsRendered(prev => ({ ...prev, [product.id]: true }));
      }).catch(err => {
        console.error(`[PayPal] Button render error for ${product.id}:`, err);
      });
    } catch (err) {
      console.error(`[PayPal] Failed to create button for ${product.id}:`, err);
    }
  }, [currentUser, navigate]);

  // Render buttons when ready
  useEffect(() => {
    if (!paypalReady || !currentUser) return;

    console.log('[PayPal] Ready to render buttons');
    
    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      PRODUCTS.forEach(product => {
        renderPayPalButton(product);
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [paypalReady, currentUser, renderPayPalButton]);

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ShoppingBag className="w-8 h-8 text-[#808080] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-3 md:px-4 py-6 md:py-10">
      <div className="max-w-6xl mx-auto space-y-6 md:space-y-10">
        <header className="text-center space-y-2 md:space-y-3">
          <div className="flex items-center justify-center gap-2 md:gap-3 text-[#d0d0d0]">
            <ShoppingBag className="w-6 h-6 md:w-8 md:h-8" />
            <h1 className="text-2xl md:text-4xl font-light">Shop</h1>
          </div>
          <p className="text-[#9a9a9a] text-sm md:text-lg px-2">
            Grab points or lock in the custom growth package. Guaranteed growth if you follow our plan exactly.
          </p>
          
          {/* Native app notice */}
          {isNativeApp && (
            <div className="bg-[#7f5af0]/10 border border-[#7f5af0]/30 rounded-xl p-3 mx-auto max-w-md">
              <p className="text-xs text-[#c8c8c8]">
                <ExternalLink className="w-3 h-3 inline mr-1" />
                Purchases are completed on our website for your security.
              </p>
            </div>
          )}
          
          {!isNativeApp && (
            <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-[#c8c8c8]">
              <ShieldCheck className="w-3 h-3 md:w-4 md:h-4" />
              <span>Payments secured by PayPal</span>
            </div>
          )}
          <div className="text-xs md:text-sm text-[#a0a0a0]">
            Your balance: <span className="text-[#d0d0d0] font-semibold">{currentUser.premium_credits || 0} credits</span>
          </div>
        </header>

        <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
          {PRODUCTS.map((product) => {
            const alreadyPurchased = product.oneTime && currentUser?.growth_package_purchased_at;
            
            return (
              <div
                key={product.id}
                className={`neuro-base rounded-2xl md:rounded-3xl p-4 md:p-6 border border-[#2a2a2a] relative ${product.highlight ? "ring-2 ring-[#7f5af0]" : ""} ${alreadyPurchased ? "opacity-75" : ""} ${product.comingSoon ? "grayscale-[70%] opacity-70" : ""}`}
              >
                {product.comingSoon && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl md:rounded-3xl z-20 pointer-events-none">
                    <span className="text-lg md:text-2xl font-bold text-[#7f5af0] bg-[#1a1a1a] px-4 md:px-6 py-2 md:py-3 rounded-xl border-2 border-[#7f5af0] rotate-[-5deg] shadow-lg">
                      COMING SOON!
                    </span>
                  </div>
                )}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="flex items-center gap-3">
                    <div className="neuro-flat rounded-xl md:rounded-2xl p-2 md:p-3">{product.icon}</div>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-[#e0e0e0]">{product.name}</h2>
                      <p className="text-xs md:text-sm text-[#909090]">{product.description}</p>
                    </div>
                  </div>
                  {product.badge && (
                    <span className={`neuro-pressed text-xs px-2 md:px-3 py-1 rounded-full whitespace-nowrap self-start ${product.comingSoon ? "text-[#7f5af0]" : alreadyPurchased ? "text-green-400" : "text-[#c0c0c0]"}`}>
                      {product.comingSoon ? "Coming Soon" : alreadyPurchased ? "✓ Purchased" : product.badge}
                    </span>
                  )}
                </div>

                <div className="mb-3 md:mb-4">
                  <div className="text-2xl md:text-3xl font-bold text-[#d0d0d0]">
                    ${product.price}
                    {product.monthly && <span className="text-sm font-normal text-[#808080]">/month</span>}
                  </div>
                  <p className="text-xs text-[#808080]">
                    {product.oneTime ? "Annual package • Includes premium tool access" : product.monthly ? "Monthly subscription • Cancel anytime" : "Single pack • billed once"} (fees added at checkout)
                  </p>
                </div>

                <ul className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
                  {product.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-xs md:text-sm text-[#c8c8c8]">
                      <Crown className="w-3 h-3 md:w-4 md:h-4 text-[#b0a090] flex-shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                {/* Learn more link for Growth Package */}
                {product.oneTime && (
                  <a 
                    href="/GrowthProgram" 
                    className="block text-center text-sm text-[#7f5af0] hover:underline mb-4"
                  >
                    Learn about the Growth Program →
                  </a>
                )}

                {/* PayPal Smart Payment Button, Native App Link, or Already Purchased Message */}
                {product.comingSoon ? (
                  <div className="text-center py-4 bg-[#2a2a2a] rounded-lg">
                    <span className="text-[#808080] text-sm">Available Soon</span>
                  </div>
                ) : alreadyPurchased ? (
                  <div className="text-center py-4 text-green-400 font-medium">
                    ✓ You're enrolled in the Growth Program
                  </div>
                ) : isNativeApp ? (
                  /* Native App: Show link to website for purchases */
                  <a
                    href={PURCHASE_WEBSITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-4 bg-[#7f5af0] hover:bg-[#6b4ed4] text-white font-medium rounded-xl transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Purchase on Website</span>
                  </a>
                ) : (
                  /* Web: Show PayPal buttons */
                  <div className="relative min-h-[55px]">
                    {processingProduct === product.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a]/80 rounded-lg z-10">
                        <Loader2 className="w-6 h-6 animate-spin text-[#d0d0d0]" />
                      </div>
                    )}
                    <div 
                      id={`paypal-button-${product.id}`} 
                      className="min-h-[50px]"
                    />
                    {!buttonsRendered[product.id] && (
                      <div className="flex items-center justify-center h-[50px] text-[#808080] text-sm">
                        {paypalReady ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          "Loading payment options..."
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
